/**
 * Casilla "Asegurar mi pedido" sobre Shopper Protect (Skydropx).
 *
 * La app de Skydropx cotiza el seguro según el subtotal y reescribe el botón de
 * pago con el escalón cotizado (data-protection-variant-id / -quote-cost). Eso
 * se le deja. El cobro ya no: su código reconoce su producto por
 * `handle === 'shopper-protect'`, pero el que usa hoy se llama
 * `shopper-protect-1`. Un seguro que se quedaba en el carrito (el cliente
 * marcó, fue al checkout y regresó) no lo reconocía: lo contaba como mercancía
 * y cotizaba un escalón más caro, no lo quitaba, y su "sin protección" tampoco.
 * Medido en Chrome real el 10-sep-2026: con la casilla desmarcada, el checkout
 * cobraba el seguro.
 *
 * Por eso aquí:
 *   · al cargar y al volver con "atrás" se quita CUALQUIER seguro que haya
 *     quedado (handle que empiece con shopper-protect);
 *   · el clic en pagar lo resuelve este script: desmarcada → el carrito sale
 *     sin seguro; marcada → exactamente un seguro, el del escalón cotizado;
 *   · el Total del carrito suma el seguro con marcada, con el precio que va a
 *     cobrar el checkout (ya con IVA), y lo resta al desmarcar.
 *
 * La casilla nace DESMARCADA (decisión del 10-sep-2026) y la elección se
 * recuerda en sessionStorage: el drawer redibuja su pie en cada cambio.
 *
 * `ma-sp-activo` en <html> autoriza al CSS a esconder lo de la app: si este
 * script no corre, se ve el widget original en vez de cobrar sin avisar.
 */
(() => {
  const CLAVE = 'ma:asegurar-pedido';
  // Los dos productos que creó Skydropx (mismos precios); su cotización usa uno.
  const PRODUCTOS_SEGURO = ['shopper-protect-1', 'shopper-protect'];
  // Con este selector busca la app los botones de pago (protection-logic.js).
  const BOTONES_DE_PAGO = '[name="checkout"], .checkout-btn, a[href*="/checkout"]';

  // La app envuelve window.fetch en DOMContentLoaded para enterarse de cada
  // cambio al carrito y volver a sincronizar — y al sincronizar QUITA el
  // seguro. Este script corre antes, así que se queda con el fetch sin
  // envoltura para lo que la app no debe ver (los cambios del momento de
  // pagar), y usa el envuelto para lo que sí (limpiar sobrantes: así vuelve a
  // cotizar sobre el carrito limpio).
  const fetchDirecto = window.fetch.bind(window);
  const fetchVisto = (...args) => window.fetch(...args);

  document.documentElement.classList.add('ma-sp-activo');

  const estado = {
    base: null, // total del carrito SIN seguros, en centavos
    version: 0, // sube cada vez que el carrito cambió y la base dejó de servir
    falloBase: 0,
    pidiendoBase: null, // la versión para la que ya hay una lectura en camino
    precios: new Map(), // variante del seguro → precio con IVA, en centavos
    pidiendoPrecios: null,
    desarmados: [],
  };

  const raiz = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const esSeguro = (item) => typeof item.handle === 'string' && item.handle.startsWith('shopper-protect');
  const dormir = (ms) => new Promise((r) => setTimeout(r, ms));

  function quiereSeguro() {
    try {
      return sessionStorage.getItem(CLAVE) === 'si';
    } catch (_) {
      return false;
    }
  }

  function recordar(si) {
    try {
      sessionStorage.setItem(CLAVE, si ? 'si' : 'no');
    } catch (_) {
      /* modo privado sin storage: la casilla vale sólo para esta vista */
    }
  }

  // El formato de dinero de la tienda (shop.money_format), p.ej.
  // "${{amount_no_decimals}} MXN". Así el Total nuevo se ve igual que el de Liquid.
  function formatear(centavos, formato) {
    const partes = (decimales, miles, sepDecimal) => {
      const [entero, fraccion] = (centavos / 100).toFixed(decimales).split('.');
      const conMiles = entero.replace(/\B(?=(\d{3})+(?!\d))/g, miles);
      return fraccion ? conMiles + sepDecimal + fraccion : conMiles;
    };
    const valores = {
      amount: () => partes(2, ',', '.'),
      amount_no_decimals: () => partes(0, ',', '.'),
      amount_with_comma_separator: () => partes(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => partes(0, '.', ','),
    };
    return (formato || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, clave) =>
      (valores[clave] || valores.amount)(),
    );
  }

  async function leerCarrito(f) {
    const r = await f(`${raiz()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error(`cart.js respondió ${r.status}`);
    return r.json();
  }

  function cambiar(f, key, quantity, extra) {
    return f(`${raiz()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity, ...extra }),
    });
  }

  // Lo que el cliente paga si NO se asegura.
  const baseDe = (carrito) =>
    carrito.items.reduce((t, i) => (esSeguro(i) ? t - (i.final_line_price || 0) : t), carrito.total_price);

  function invalidarBase() {
    estado.base = null;
    estado.version += 1;
  }

  // Una lectura por versión. Si el carrito vuelve a cambiar mientras viene en
  // camino, su resultado ya es viejo y se descarta: sin esto, una respuesta
  // tardía pisaría la base del carrito nuevo.
  function pedirBase() {
    const version = estado.version;
    if (estado.pidiendoBase === version || Date.now() - estado.falloBase < 10000) return;
    estado.pidiendoBase = version;
    leerCarrito(fetchDirecto)
      .then((c) => {
        if (estado.version === version) estado.base = baseDe(c);
      })
      .catch(() => {
        estado.falloBase = Date.now();
      })
      .finally(() => {
        if (estado.pidiendoBase === version) estado.pidiendoBase = null;
        agendar();
      });
  }

  // Una vez por página: el precio con IVA de cada escalón, tal como lo cobra el
  // checkout. Sin él no se toca el Total — un total inventado es peor que uno viejo.
  function pedirPrecios() {
    if (estado.pidiendoPrecios) return;
    estado.pidiendoPrecios = Promise.all(
      PRODUCTOS_SEGURO.map((handle) =>
        fetchDirecto(`${raiz()}products/${handle}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null),
      ),
    )
      .then((productos) => {
        productos.forEach((p) =>
          ((p && p.variants) || []).forEach((v) => estado.precios.set(String(v.id), v.price)),
        );
      })
      .finally(() => agendar());
  }

  // Idempotente y sin escribir si nada cambió: corre desde un MutationObserver,
  // y cada escritura innecesaria despertaría también al observador de la app.
  function sincronizar() {
    const marcado = quiereSeguro();
    let faltaBase = false;
    let faltaPrecio = false;

    document.querySelectorAll('[data-ma-sp]').forEach((fila) => {
      const zona = fila.closest('[data-ma-sp-scope]');
      if (!zona) return;
      const boton = zona.querySelector('[data-protection-variant-id]');
      const costo = boton ? boton.getAttribute('data-protection-quote-cost') : null;
      const variante = boton ? boton.getAttribute('data-protection-variant-id') : null;

      const ocultar = !costo;
      if (fila.hidden !== ocultar) fila.hidden = ocultar;
      if (costo) {
        const el = fila.querySelector('[data-ma-sp-costo]');
        const texto = `$${costo} + IVA`;
        if (el && el.textContent !== texto) el.textContent = texto;
      }
      if (variante && !estado.precios.has(variante)) faltaPrecio = true;

      const casilla = fila.querySelector('[data-ma-sp-check]');
      if (casilla && casilla.checked !== marcado) casilla.checked = marcado;

      const total = zona.querySelector('[data-ma-sp-total]');
      if (!total) return;
      // Un total que no escribimos nosotros es un carrito que cambió (render
      // nuevo del pie, o el refresh del drawer): la base guardada ya no sirve.
      // Se marca como visto en el acto: si no, al llegar la base nueva este
      // mismo total —que aún no reescribimos— la volvería a tirar, y así sin fin
      // (lo cazó la prueba: se colgaba pidiendo /cart.js).
      if (total.dataset.maSpMostrado === undefined || total.textContent !== total.dataset.maSpMostrado) {
        invalidarBase();
        total.dataset.maSpMostrado = total.textContent;
      }
      if (estado.base === null) {
        faltaBase = true;
        return;
      }
      let extra = 0;
      if (marcado && costo && variante) {
        if (!estado.precios.has(variante)) return; // se escribe cuando llegue el precio real
        extra = estado.precios.get(variante);
      }
      const texto = formatear(estado.base + extra, fila.dataset.maSpFormato);
      if (total.textContent !== texto) total.textContent = texto;
      total.dataset.maSpMostrado = texto;
    });

    if (faltaBase) pedirBase();
    if (faltaPrecio) pedirPrecios();
  }

  // El observador sólo AGENDA la sincronización y junta la ráfaga en una sola
  // pasada: la app dispara decenas de mutaciones seguidas al pintar su botón, y
  // escribir desde dentro del callback reentraría en entornos donde el
  // observador corre síncrono (happy-dom lo hace; así se descubrió).
  let agendada = false;
  function agendar() {
    if (agendada) return;
    agendada = true;
    queueMicrotask(() => {
      agendada = false;
      sincronizar();
    });
  }

  // Quita del carrito cualquier seguro que haya quedado de una ida al checkout.
  async function limpiarSobrantes() {
    try {
      const carrito = await leerCarrito(fetchDirecto);
      const sobrantes = carrito.items.filter(esSeguro);
      if (!sobrantes.length) return;
      let respuesta = null;
      for (const item of sobrantes) {
        respuesta = await cambiar(fetchVisto, item.key, 0, { sections: 'cart-drawer' });
      }
      if (!respuesta || !respuesta.ok) return;
      // /cart pinta sus líneas desde el servidor: se recarga una vez, ya limpio.
      if (document.querySelector('[data-cart-page]')) {
        window.location.reload();
        return;
      }
      const nuevo = await respuesta.json();
      const html = nuevo && nuevo.sections && nuevo.sections['cart-drawer'];
      if (html && window.MaCart && window.MaCart.renderSection) {
        window.MaCart.renderSection(html, nuevo.item_count);
      }
      invalidarBase();
      agendar();
    } catch (err) {
      console.error('Shopper Protect: no se pudo limpiar el carrito', err);
    }
  }

  // Deja en el carrito exactamente lo elegido: ningún seguro, o uno solo del
  // escalón cotizado. Por el fetch que la app no ve.
  async function dejarSeguro(variante) {
    const carrito = await leerCarrito(fetchDirecto);
    let yaEsta = false;
    for (const item of carrito.items.filter(esSeguro)) {
      if (variante && !yaEsta && String(item.variant_id) === String(variante)) {
        yaEsta = true;
        if (item.quantity !== 1) await cambiar(fetchDirecto, item.key, 1);
      } else {
        await cambiar(fetchDirecto, item.key, 0);
      }
    }
    if (variante && !yaEsta) {
      const r = await fetchDirecto(`${raiz()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variante), quantity: 1 }] }),
      });
      if (!r.ok) throw new Error(`no se pudo agregar el seguro (${r.status})`);
    }
  }

  // Sin botones de pago a la vista, la app no arranca otra sincronización (y
  // no quita el seguro recién agregado). Se vuelven a armar al regresar.
  function desarmar() {
    document.querySelectorAll('[data-ma-sp-scope]').forEach((zona) => {
      zona.querySelectorAll(BOTONES_DE_PAGO).forEach((b) => {
        estado.desarmados.push({ b, href: b.getAttribute('href'), name: b.getAttribute('name') });
        b.setAttribute('aria-busy', 'true');
        b.removeAttribute('href');
        b.removeAttribute('name');
      });
    });
  }

  function rearmar() {
    estado.desarmados.splice(0).forEach(({ b, href, name }) => {
      if (href !== null) b.setAttribute('href', href);
      if (name !== null) b.setAttribute('name', name);
      b.removeAttribute('aria-busy');
    });
  }

  function irAlCheckout(boton) {
    const form = boton.closest('form');
    if (form && /\/cart/.test(form.getAttribute('action') || '')) {
      // form.submit() no manda el botón que lo disparó; sin `checkout` Shopify
      // sólo guarda el carrito (y la nota) y regresa a /cart.
      if (!form.querySelector('input[type="hidden"][name="checkout"]')) {
        const intento = document.createElement('input');
        intento.type = 'hidden';
        intento.name = 'checkout';
        intento.value = '';
        form.appendChild(intento);
      }
      form.submit();
    } else {
      window.location.assign(`${raiz()}checkout`);
    }
    // Si por algo no se fue, que el botón no se quede muerto.
    setTimeout(rearmar, 8000);
  }

  async function pagar(boton, variante) {
    desarmar();
    try {
      const drawer = boton.closest('ma-cart-drawer');
      if (drawer && typeof drawer._saveNote === 'function') await drawer._saveNote();
      // Una sincronización de la app que ya iba en camino podría ver el seguro
      // recién agregado y quitarlo; con los botones desarmados ya no empieza
      // otra, y se le da tiempo a terminar a la que estuviera en vuelo.
      await dormir(350);
      await dejarSeguro(variante);
    } catch (err) {
      console.error('Shopper Protect: no se pudo ajustar el seguro antes del checkout', err);
    }
    irAlCheckout(boton);
  }

  new MutationObserver(agendar).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-protection-quote-cost', 'data-protection-variant-id'],
  });

  document.addEventListener('change', (e) => {
    const casilla = e.target.closest && e.target.closest('[data-ma-sp-check]');
    if (!casilla) return;
    recordar(casilla.checked);
    sincronizar();
  });

  // En captura: corre antes que el drawer (que guardaría la nota y navegaría)
  // y que la app (que agregaría el seguro a su manera). Aquí se hacen las dos.
  document.addEventListener(
    'click',
    (e) => {
      const boton = e.target.closest && e.target.closest('[data-protection-variant-id]');
      if (!boton) return;
      const zona = boton.closest('[data-ma-sp-scope]');
      const casilla = zona && zona.querySelector('[data-ma-sp-check]');
      if (!casilla) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (boton.getAttribute('aria-busy') === 'true') return;
      pagar(boton, casilla.checked ? boton.getAttribute('data-protection-variant-id') : null);
    },
    true,
  );

  // Volver del checkout con "atrás" restaura la página congelada: botones
  // desarmados y, si iba asegurado, el seguro en el carrito.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    rearmar();
    invalidarBase();
    limpiarSobrantes();
    agendar();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', limpiarSobrantes);
  } else {
    limpiarSobrantes();
  }

  sincronizar();
})();
